import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePolizaForm } from './formularioEmisionPolizas/usePolizaForm';

// --- IMPORTACIÓN DE SECCIONES ---
import SectionRiesgo from './formularioEmisionPolizas/SectionRiesgo';
import SectionCompania from './formularioEmisionPolizas/SectionCompania';
import SectionRenovacionPack from './formularioEmisionPolizas/SectionRenovacionPack';
import SectionComercial from './formularioEmisionPolizas/SectionComercial';
import SectionFacturacion from './formularioEmisionPolizas/SectionFacturacion';
import SectionAdicional from './formularioEmisionPolizas/SectionAdicional';

const FormularioPoliza = ({ cliente, clientData, tipoCarga, mode, onCancel, onSuccess, onNewCarga, initialData = null }) => {
    const navigate = useNavigate();
    const clienteActivo = cliente || clientData;
    const [successData, setSuccessData] = useState(null);

    const { state, actions } = usePolizaForm(clienteActivo, mode, initialData, tipoCarga, (id, data) => {
        // Al guardar con éxito, seteamos los datos para mostrar la pantalla de éxito
        setSuccessData({ id, ...data });
        if (onSuccess) onSuccess('Póliza procesada con éxito', { id, ...data });
    });

    /**
     * PROTECCIÓN CONTRA VALORES NULOS
     */
    if (!clienteActivo) {
        return (
            <div className="flex-grow flex items-center justify-center p-8 text-slate-500 italic bg-white h-full min-h-0">
                <div className="flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    <span>Cargando datos del asegurado...</span>
                </div>
            </div>
        );
    }

    /**
     * PANTALLA DE ÉXITO
     */
    if (successData) {
        return (
            <div className="flex flex-col h-full bg-white overflow-y-auto custom-scrollbar">
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center animate-fade-in my-auto">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-inner">
                        <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-800 mb-2">¡Emisión Exitosa!</h2>
                    <p className="text-slate-500 text-base mb-1">
                        La póliza <span className="font-mono font-bold text-blue-600">#{successData.nPoliza}</span> se guardó correctamente.
                    </p>
                    <p className="text-slate-400 mb-8 italic text-sm">
                        Cliente: {clienteActivo?.nombre} {clienteActivo?.apellido}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md justify-center">
                        <button 
                            onClick={() => navigate(`/clientes/${clienteActivo.id}/polizas/${successData.id}`)} 
                            className="flex-1 bg-slate-800 text-white px-5 py-3 rounded-xl font-bold shadow-lg hover:bg-slate-900 transition active:scale-95 flex items-center justify-center gap-2 text-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Ver Detalle
                        </button>

                        <button 
                            onClick={onNewCarga}
                            className="flex-1 bg-indigo-600 text-white px-5 py-3 rounded-xl font-bold shadow-lg hover:bg-indigo-700 transition active:scale-95 flex items-center justify-center gap-2 text-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                            Cargar otra
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={actions.handleSubmit} className="flex flex-col h-full overflow-hidden text-left bg-white min-h-0">
            
            {/* 1. CUERPO CON SCROLL (Agregado min-h-0 para forzar el comportamiento de recorte) */}
            <div className="flex-grow overflow-y-auto p-8 space-y-8 custom-scrollbar min-h-0">
                {mode === 'renewal' && (
                    <div className="bg-blue-600 text-white p-4 rounded-xl flex items-center gap-3 shadow-md animate-fade-in">
                        <div className="bg-white/20 p-2 rounded-lg font-black uppercase text-[10px]">Modo Renovación</div>
                        <span className="font-bold">Origen: {initialData?.nPoliza}</span>
                    </div>
                )}

                <SectionRiesgo state={state} actions={actions} />
                <SectionCompania state={state} actions={actions} />
                <SectionRenovacionPack state={state} actions={actions} />
                <SectionComercial state={state} actions={actions} />
                <SectionFacturacion state={state} actions={actions} clienteActivo={clienteActivo} />
                <SectionAdicional state={state} actions={actions} />

                <fieldset className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
                    <legend className="px-3 text-lg font-bold text-gray-700">Observaciones Internas</legend>
                    <textarea 
                        name="observaciones" 
                        value={state.formData.observaciones || ''}
                        onChange={actions.handleChange} 
                        className="input-round min-h-[120px] py-3 resize-none"
                        placeholder="Notas importantes sobre esta póliza..."
                    />
                </fieldset>
            </div>

            {/* 2. FOOTER FIJO DE ACCIONES */}
            <div className="shrink-0 flex justify-end gap-4 p-6 border-t border-gray-100 bg-slate-50">
                <button type="button" onClick={onCancel} className="btn-secondary">
                    Cancelar
                </button>
                <button 
                    type="submit" 
                    disabled={state.isSubmitting} 
                    className="btn-primary"
                >
                    {state.isSubmitting ? 'Procesando...' : mode === 'edit' ? 'Guardar Cambios' : 'Confirmar Póliza'}
                </button>
            </div>

            <style>{`
                .label-form { display: block; font-size: 0.875rem; font-weight: 700; color: #4b5563; margin-bottom: 0.25rem; }
                .input-round { width: 100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; outline: none; transition: all 0.2s; font-size: 0.95rem; background-color: #fff; height: 46px; }
                .input-round:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
                .btn-primary { padding: 0.75rem 2.5rem; border-radius: 0.75rem; background-color: #2563eb; color: #fff; font-weight: 700; text-transform: uppercase; font-size: 0.75rem; transition: all 0.2s; cursor: pointer; border: none; }
                .btn-primary:hover:not(:disabled) { background-color: #1d4ed8; transform: translateY(-1px); }
                .btn-primary:disabled { background-color: #93c5fd; cursor: not-allowed; }
                .btn-secondary { padding: 0.75rem 1.5rem; border-radius: 0.75rem; border: 1px solid #d1d5db; font-weight: 700; color: #4b5563; text-transform: uppercase; font-size: 0.75rem; transition: all 0.2s; cursor: pointer; background: #fff; }
                .btn-secondary:hover { background-color: #f9fafb; }
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
            `}</style>
        </form>
    );
};

export default FormularioPoliza;